import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { Partner } from '../../../../shared-types/src/index.js';
import type { CapabilitySourceRecord, Tenant, ToastMessage } from '../../types.js';

const DEFAULT_AGENT_BUNDLES_SOURCE_KEY = 'syleeveluga/agent_bundles@main';

/**
 * Platform "Capability sources" tab (skill-agent-distribution PR10). Registers and
 * lists externally-owned skill/agent-team source repos.
 */
export function CapabilitySourcesTab({
  http,
  onToast,
  isActive,
  tenant
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  isActive: boolean;
  tenant?: Tenant;
}) {
  const { t } = useTranslation();
  const [sources, setSources] = useState<CapabilitySourceRecord[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [selectedTenantSourceId, setSelectedTenantSourceId] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [ref, setRef] = useState('main');
  const [displayName, setDisplayName] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [requireSignature, setRequireSignature] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [nextSources, nextPartners] = await Promise.all([
        http.listCapabilitySources(),
        http.listPartners()
      ]);
      const defaultSource = nextSources.find(
        (source) => source.sourceKey === DEFAULT_AGENT_BUNDLES_SOURCE_KEY
      );
      const nextAssignableSources = nextSources.filter(
        (source) => source.status === 'active' && !source.tenantId
      );
      setSources(nextSources);
      setPartners(nextPartners);
      setSelectedPartnerId((current) => current || (nextPartners[0]?.id ?? ''));
      setSelectedSourceId(
        (current) =>
          nextAssignableSources.some((source) => source.id === current)
            ? current
            : (nextAssignableSources.find(
                (source) => source.id === defaultSource?.id
              )?.id ?? nextAssignableSources[0]?.id ?? '')
      );
      setSelectedTenantSourceId(
        (current) =>
          nextSources.some((source) => source.id === current)
            ? current
            : (defaultSource?.id ?? nextSources[0]?.id ?? '')
      );
    } catch (err) {
      onToast({ tone: 'error', message: describeError(err, t) });
    } finally {
      setLoading(false);
    }
  }, [http, onToast, t]);

  useEffect(() => {
    if (isActive) void refresh();
  }, [isActive, refresh]);

  const register = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault();
      setBusy(true);
      try {
        await http.registerCapabilitySource({
          sourceKind: 'skill-pack',
          displayName: displayName.trim() || repoUrl.trim(),
          repoUrl: repoUrl.trim(),
          ref: ref.trim() || 'main',
          visibility: 'public',
          partnerId: partnerId.trim() || undefined,
          requireSignature
        });
        setRepoUrl('');
        setDisplayName('');
        setPartnerId('');
        onToast({
          tone: 'success',
          message: t('capabilitySources.sourceRegistered')
        });
        await refresh();
      } catch (err) {
        onToast({ tone: 'error', message: describeError(err, t) });
      } finally {
        setBusy(false);
      }
    },
    [
      displayName,
      http,
      onToast,
      partnerId,
      ref,
      refresh,
      repoUrl,
      requireSignature,
      t
    ]
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await http.deleteCapabilitySource(id);
        await refresh();
      } catch (err) {
        onToast({ tone: 'error', message: describeError(err, t) });
      }
    },
    [http, onToast, refresh, t]
  );

  const assignPartnerSource = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault();
      const sourceId =
        selectedSourceId ||
        sources.find(
          (source) => source.sourceKey === DEFAULT_AGENT_BUNDLES_SOURCE_KEY
        )?.id ||
        sources[0]?.id ||
        '';
      if (!selectedPartnerId || !sourceId) return;
      setBusy(true);
      try {
        await http.setPartnerCapabilitySourceScope(selectedPartnerId, [
          sourceId
        ]);
        onToast({
          tone: 'success',
          message: t('capabilitySources.sourceAssigned')
        });
      } catch (err) {
        onToast({ tone: 'error', message: describeError(err, t) });
      } finally {
        setBusy(false);
      }
    },
    [http, onToast, selectedPartnerId, selectedSourceId, sources, t]
  );

  const assignableSources = sources.filter(
    (source) => source.status === 'active' && !source.tenantId
  );
  const tenantSource = sources.find(
    (source) => source.id === selectedTenantSourceId
  );
  const tenantSourceIsAssignable = Boolean(
    tenantSource &&
      tenantSource.status === 'active' &&
      !tenantSource.tenantId &&
      (!tenantSource.partnerId || tenantSource.partnerId === tenant?.partnerId)
  );

  const assignTenantSource = useCallback(async () => {
    if (!tenant || !selectedTenantSourceId || !tenantSourceIsAssignable) return;
    setBusy(true);
    try {
      await http.assignTenantCapabilitySource(tenant.id, selectedTenantSourceId);
      onToast({
        tone: 'success',
        message: t('capabilitySources.sourceAssignedToTenant')
      });
    } catch (err) {
      onToast({ tone: 'error', message: describeError(err, t) });
    } finally {
      setBusy(false);
    }
  }, [http, onToast, selectedTenantSourceId, t, tenant, tenantSourceIsAssignable]);

  const revokeTenantSource = useCallback(async () => {
    if (!tenant || !selectedTenantSourceId) return;
    setBusy(true);
    try {
      await http.revokeTenantCapabilitySource(tenant.id, selectedTenantSourceId);
      onToast({
        tone: 'success',
        message: t('capabilitySources.sourceRevokedFromTenant')
      });
    } catch (err) {
      onToast({ tone: 'error', message: describeError(err, t) });
    } finally {
      setBusy(false);
    }
  }, [http, onToast, selectedTenantSourceId, t, tenant]);

  return (
    <section className="content-stack">
      <section className="panel form-grid">
        <h2>{t('capabilitySources.tenantAssignTitle')}</h2>
        <p className="screen-help">{t('capabilitySources.tenantAssignHelp')}</p>
        <p>
          <strong>{t('capabilitySources.targetTenant')}: </strong>
          {tenant
            ? `${tenant.name} (${tenant.id})`
            : t('capabilitySources.noTargetTenant')}
        </p>
        <label>
          {t('capabilitySources.source')}
          <select
            value={selectedTenantSourceId}
            onChange={(event) => setSelectedTenantSourceId(event.target.value)}
            disabled={loading || sources.length === 0}
          >
            {sources.length === 0 && (
              <option value="">
                {loading
                  ? t('capabilitySources.loading')
                  : t('capabilitySources.noAssignableSources')}
              </option>
            )}
            {sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.displayName} ({source.sourceKey})
              </option>
            ))}
          </select>
        </label>
        <div className="form-actions">
          <button
            type="button"
            disabled={busy || loading || !tenant || !tenantSourceIsAssignable}
            onClick={() => void assignTenantSource()}
          >
            {t('capabilitySources.assign')}
          </button>
          <button
            type="button"
            className="danger"
            disabled={busy || loading || !tenant || !selectedTenantSourceId}
            onClick={() => void revokeTenantSource()}
          >
            {t('capabilitySources.revoke')}
          </button>
        </div>
      </section>

      <form className="panel form-grid" onSubmit={register}>
        <h2>{t('capabilitySources.registerTitle')}</h2>
        <p className="screen-help">{t('capabilitySources.registerHelp')}</p>
        <label>
          {t('capabilitySources.repoUrl')}
          <span>
            <input
              placeholder={t('capabilitySources.repoUrlPlaceholder')}
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
            />
            <small className="field-help">
              {t('capabilitySources.repoUrlHelp')}
            </small>
          </span>
        </label>
        <label>
          {t('capabilitySources.ref')}
          <span>
            <input value={ref} onChange={(e) => setRef(e.target.value)} />
            <small className="field-help">
              {t('capabilitySources.refHelp')}
            </small>
          </span>
        </label>
        <label>
          {t('capabilitySources.displayName')}
          <span>
            <input
              placeholder={t('capabilitySources.displayNamePlaceholder')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <small className="field-help">
              {t('capabilitySources.displayNameHelp')}
            </small>
          </span>
        </label>
        <label>
          {t('capabilitySources.scope')}
          <span>
            <select
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            >
              <option value="">{t('capabilitySources.platformGlobal')}</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name} ({partner.id})
                </option>
              ))}
            </select>
            <small className="field-help">
              {t('capabilitySources.scopeHelp')}
            </small>
          </span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={requireSignature}
            onChange={(e) => setRequireSignature(e.target.checked)}
          />
          <span>
            <strong>{t('capabilitySources.requireSignature')}</strong>
            <small className="field-help">
              {t('capabilitySources.requireSignatureHelp')}
            </small>
          </span>
        </label>
        <div className="form-actions">
          <button disabled={busy || !repoUrl.trim()}>
            {t('common.register')}
          </button>
        </div>
      </form>

      {partners.length > 0 && (
        <form className="panel form-grid" onSubmit={assignPartnerSource}>
          <h2>{t('capabilitySources.assignTitle')}</h2>
          <p className="screen-help">{t('capabilitySources.assignHelp')}</p>
          <label>
            {t('capabilitySources.partner')}
            <select
              value={selectedPartnerId}
              onChange={(e) => setSelectedPartnerId(e.target.value)}
            >
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name} ({partner.id})
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('capabilitySources.source')}
            <select
              value={selectedSourceId}
              onChange={(e) => setSelectedSourceId(e.target.value)}
            >
              {assignableSources.length === 0 && (
                <option value="">
                  {t('capabilitySources.noAssignableSources')}
                </option>
              )}
              {assignableSources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.displayName} ({source.sourceKey})
                </option>
              ))}
            </select>
          </label>
          <div className="form-actions">
            <button
              disabled={
                busy || !selectedPartnerId || assignableSources.length === 0
              }
            >
              {t('capabilitySources.assign')}
            </button>
          </div>
        </form>
      )}

      <section className="panel">
        <h2>{t('capabilitySources.listTitle')}</h2>
        <div className="credential-list">
          {loading && (
            <p className="empty-state">{t('capabilitySources.loading')}</p>
          )}
          {sources.map((source) => (
            <div key={source.id} className="credential-row">
              <span className="credential-info">
                <strong>{source.displayName}</strong>
                <small>({source.sourceKey})</small>
                <small>
                  {source.partnerId
                    ? t('capabilitySources.partnerScope', {
                        partnerId: source.partnerId
                      })
                    : t('capabilitySources.platformGlobal')}{' '}
                  -{' '}
                  {t(`capabilitySources.status.${source.status}`, {
                    defaultValue: source.status
                  })}
                  {source.lastSyncStatus
                    ? ` - ${t('capabilitySources.syncStatus', { status: source.lastSyncStatus })}`
                    : ''}
                </small>
              </span>
              <span className="credential-actions">
                <button
                  className="danger"
                  onClick={() => void remove(source.id)}
                >
                  {t('capabilitySources.delete')}
                </button>
              </span>
            </div>
          ))}
          {!loading && sources.length === 0 && (
            <p className="empty-state">{t('capabilitySources.empty')}</p>
          )}
        </div>
      </section>
    </section>
  );
}
